import { type PropsWithChildren, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { Me } from '../App.js';
import { UploadDrawer, useUploads } from '../features/uploads/UploadProvider.js';
import styles from './Shell.module.css';

const Arrow = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>;

export function Shell({ me, children }: PropsWithChildren<{ me: Me }>) {
  const location = useLocation();
  const uploadInput = useRef<HTMLInputElement>(null);
  const { addFiles } = useUploads();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const active = (path: string) => location.pathname === path;
  const albumSurface = location.pathname === '/' || location.pathname === '/new' || location.pathname === '/highlights' || location.pathname === '/keepsakes' || (location.pathname.startsWith('/people/') && location.pathname !== '/people');

  async function selected(files: FileList | null) {
    if (!files?.length) return;
    setUploadError('');
    try {
      await addFiles([...files]);
      setUploadOpen(false);
    } catch (caught) {
      setUploadError(caught instanceof Error ? caught.message : 'Could not add those files.');
    }
  }

  return <div className={styles.shell}>
    <aside className={styles.sidebar} aria-label="Album index">
      <p className={styles.kicker}>Char & Nick · 08.08.26</p>
      <h1 className={styles.brand}>The wedding<br />album</h1>

      <section className={styles.indexSection} aria-labelledby="who-heading">
        <h2 id="who-heading">Who</h2>
        <Link className={active('/') ? styles.active : undefined} to="/"><span>Everyone</span><span className={styles.dots} /><small>All</small></Link>
        <Link className={active(`/people/${me.id}`) ? styles.active : undefined} to={`/people/${me.id}`}><span>Just me</span><span className={styles.dots} /><small>Mine</small></Link>
      </section>

      <section className={styles.indexSection} aria-labelledby="when-heading">
        <h2 id="when-heading">When</h2>
        <Link className={active('/') ? styles.active : undefined} to="/"><span><small>—</small> The whole night</span><span className={styles.dots} /><small>All</small></Link>
        <Link className={active('/new') ? styles.active : undefined} to="/new"><span><small>I</small> New photographs</span><span className={styles.dots} /><small>{me.newCount || '—'}</small></Link>
        <Link className={active('/highlights') ? styles.active : undefined} to="/highlights"><span><small>II</small> Highlights</span><span className={styles.dots} /><small>—</small></Link>
        <Link className={active('/keepsakes') ? styles.active : undefined} to="/keepsakes"><span><small>III</small> Keepsakes</span><span className={styles.dots} /><small>—</small></Link>
      </section>

      <nav className={styles.utility} aria-label="Album utilities">
        <Link to="/?select=1"><span>Select photographs</span><Arrow /></Link>
        <Link to="/more"><span>Your account</span><Arrow /></Link>
        {me.isAdmin ? <Link to="/admin"><span>Admin</span><Arrow /></Link> : null}
      </nav>
      <p className={styles.account}>{me.displayName}</p>
    </aside>

    <main id="main-content" className={styles.content}>{children}</main>
    {albumSurface ? <button className={styles.addStrip} onClick={() => setUploadOpen(true)}><span>+ Add your photos</span><Arrow /></button> : null}
    {uploadOpen ? <div className={styles.uploadLayer} role="presentation">
      <section className={styles.uploadSheet} role="dialog" aria-modal="true" aria-labelledby="add-photos-title">
        <header><div><p>From your camera roll</p><h2 id="add-photos-title">Add to the album</h2></div><button className={styles.close} aria-label="Close" onClick={() => setUploadOpen(false)}>×</button></header>
        <p className={styles.uploadCopy}>Anything you took on the 8th — photos or video. They land in the right chapter by themselves.</p>
        <input ref={uploadInput} hidden type="file" multiple accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.mp4,.mov" onChange={event => void selected(event.target.files)} />
        <button className={styles.choosePhotos} onClick={() => uploadInput.current?.click()}>Upload</button>
        {uploadError ? <p role="alert" className="error">{uploadError}</p> : null}
      </section>
    </div> : null}
    <UploadDrawer />
  </div>;
}
